import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, User, Shield, Phone, Building, Wallet, GraduationCap,
  Award, Briefcase, Users, FileText, CheckCircle2, AlertCircle,
  Download, Printer, Mail, Sparkles, Loader2, Eye, EyeOff, RefreshCw, FileCheck
} from 'lucide-react';
import { AuthenticatedImage } from './AuthenticatedImage';
import { Avatar, AvatarImage, AvatarFallback } from './ui/avatar';

const API_URL = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

export default function EmployeeViewModal({ employeeId, onClose }) {
  const [activeTab, setActiveTab] = useState('details');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [approving, setApproving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);

  const fetchPdfBlob = async (empId) => {
    try {
      const res = await axios.get(`${API_URL}/employees/${empId || employeeId}/appointment-letter/pdf`, {
        responseType: 'blob',
        withCredentials: true,
      });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      return URL.createObjectURL(blob);
    } catch (err) {
      console.error('Failed to load PDF blob:', err);
      return null;
    }
  };

  const fetchFullDetails = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/employees/${employeeId}/full-details`, { withCredentials: true });
      setData(res.data);
      if (res.data?.appointment_letter?.generated || res.data?.appointment?.generated) {
        const blobUrl = await fetchPdfBlob(employeeId);
        if (blobUrl) setPdfPreviewUrl(blobUrl);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load employee profile details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (employeeId) {
      fetchFullDetails();
    }
  }, [employeeId]);

  const handleApproveCTC = async () => {
    try {
      setApproving(true);
      const res = await axios.post(`${API_URL}/employees/${employeeId}/ctc/approve`, { remarks: 'Approved from Employee View' }, { withCredentials: true });
      toast.success(res.data.message || 'CTC breakup approved successfully!');
      fetchFullDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve CTC');
    } finally {
      setApproving(false);
    }
  };

  const handleGenerateAppointmentLetter = async () => {
    try {
      setGenerating(true);
      const res = await axios.post(`${API_URL}/employees/${employeeId}/appointment-letter/generate`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Official Letter generated successfully!');
      const blobUrl = await fetchPdfBlob(employeeId);
      if (blobUrl) setPdfPreviewUrl(blobUrl);
      fetchFullDetails();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate official letter');
    } finally {
      setGenerating(false);
    }
  };

  const handleEmailAppointmentLetter = async () => {
    try {
      setEmailing(true);
      const res = await axios.post(`${API_URL}/employees/${employeeId}/appointment-letter/send-email`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Official Letter emailed successfully.');
    } catch (err) {
      const errMsg = err.response?.data?.detail || err.response?.data?.message || 'Failed to email official letter';
      toast.error(errMsg);
    } finally {
      setEmailing(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      let url = pdfPreviewUrl;
      if (!url || !url.startsWith('blob:')) {
        url = await fetchPdfBlob(employeeId);
      }
      if (!url) {
        toast.error('PDF not available for download');
        return;
      }
      const a = document.createElement('a');
      a.href = url;
      const empCode = data?.employment?.employee_code || data?.employee?.employee_code || 'EMP';
      a.download = `Appointment_Letter_${empCode}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success('PDF downloaded');
    } catch (err) {
      toast.error('Failed to download PDF');
    }
  };

  const handlePrint = async () => {
    try {
      let url = pdfPreviewUrl;
      if (!url || !url.startsWith('blob:')) {
        url = await fetchPdfBlob(employeeId);
      }
      if (!url) {
        toast.error('PDF not available for print');
        return;
      }
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.focus();
      }
    } catch (err) {
      toast.error('Failed to print PDF');
    }
  };

  if (!employeeId) return null;

  const emp = data?.employee || {};
  const pd = data?.personal || emp.personal_details || {};
  const ed = data?.employment || emp.employment_details || {};
  const cd = data?.contact || emp.contact_details || {};
  const bd = data?.bank || emp.bank_details || {};
  const kyc = data?.statutory || emp.kyc_statutory_details || {};
  const ctc = data?.ctc || data?.salary || {};
  const company = data?.company || {};
  const apptLetter = data?.appointment || data?.appointment_letter || {};

  const isCTCApproved = ctc.approval_status === 'approved' || ctc.structure_source === 'manual' || ctc.structure_source === 'group' || ctc.structure_source === 'employee_custom' || Number(ctc.ctc_annual) > 0;

  const calcAge = (dob) => {
    if (!dob) return '';
    try {
      const birth = new Date(dob);
      if (isNaN(birth.getTime())) return '';
      const now = new Date();
      let age = now.getFullYear() - birth.getFullYear();
      const m = now.getMonth() - birth.getMonth();
      if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
      return age > 0 ? `${age} Yrs` : '';
    } catch {
      return '';
    }
  };

  const dobVal = pd.date_of_birth || pd.dob || emp.date_of_birth || emp.dob;
  const ageDisplay = pd.age ? `${pd.age} Yrs` : calcAge(dobVal);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3 md:p-6" data-testid="employee-view-modal">
      <div className="bg-white rounded-xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden border border-slate-200">
        
        {/* Header Bar */}
        <div className="bg-slate-900 text-white p-4 md:p-5 flex items-center justify-between shrink-0">
          {loading ? (
            <div className="flex items-center space-x-3">
              <Loader2 className="w-5 h-5 animate-spin text-blue-400" />
              <span className="text-sm font-medium">Loading Employee 360° Profile…</span>
            </div>
          ) : (
            <div className="flex items-center space-x-4">
              <Avatar className="h-12 w-12 border-2 border-blue-500 shadow flex items-center justify-center overflow-hidden bg-slate-800 shrink-0">
                <AuthenticatedImage
                  src={pd.photoUrl || pd.photo_url || emp.photoUrl || emp.photo_url}
                  alt={pd.first_name || 'Emp'}
                  className="h-full w-full object-cover rounded-full"
                  fallbackText={((pd.first_name?.[0] || emp.first_name?.[0] || '') + (pd.last_name?.[0] || emp.last_name?.[0] || '')).toUpperCase() || 'EMP'}
                />
              </Avatar>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base md:text-lg font-bold tracking-tight">
                    {pd.first_name || emp.first_name} {pd.last_name || emp.last_name}
                  </h2>
                  <span className="bg-blue-600/30 text-blue-300 border border-blue-400/40 text-[10px] font-mono font-semibold px-2 py-0.5 rounded">
                    {ed.employee_code || emp.employee_code || 'EMP'}
                  </span>
                  <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full border uppercase ${
                    (ed.employment_status || emp.employment_status) === 'active' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40' : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                  }`}>
                    {ed.employment_status || emp.employment_status || 'Active'}
                  </span>
                </div>
                <p className="text-xs text-slate-400 mt-0.5 flex items-center gap-2">
                  <span>{ed.designation_name || ed.designation || emp.designation || 'Designation N/A'}</span>
                  <span>•</span>
                  <span>{ed.department_name || ed.department || emp.department || 'Department N/A'}</span>
                  <span>•</span>
                  <span>{company.name || 'Company'}</span>
                </p>
              </div>
            </div>
          )}
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition" data-testid="close-employee-view-modal">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="bg-slate-100 border-b border-slate-200 px-5 flex items-center gap-2 shrink-0">
          <button
            onClick={() => setActiveTab('details')}
            data-testid="tab-employee-details"
            className={`py-3 px-4 text-xs font-semibold flex items-center gap-2 border-b-2 transition ${
              activeTab === 'details'
                ? 'border-[#0052FF] text-[#0052FF] bg-white shadow-sm rounded-t-md'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <User className="w-4 h-4" />
            Tab 1 – Employee Details
          </button>

          <button
            onClick={() => setActiveTab('ctc')}
            data-testid="tab-ctc-breakup"
            className={`py-3 px-4 text-xs font-semibold flex items-center gap-2 border-b-2 transition ${
              activeTab === 'ctc'
                ? 'border-[#0052FF] text-[#0052FF] bg-white shadow-sm rounded-t-md'
                : 'border-transparent text-slate-600 hover:text-slate-900'
            }`}
          >
            <Wallet className="w-4 h-4" />
            Tab 2 – Approved CTC &amp; Official Letter
            {apptLetter.generated && (
              <span className="bg-emerald-100 text-emerald-800 text-[10px] font-bold px-1.5 py-0.2 rounded-full border border-emerald-300">
                Generated
              </span>
            )}
          </button>
        </div>

        {/* Content Body */}
        <div className="flex-1 overflow-y-auto p-5 bg-slate-50 space-y-6">
          {loading ? (
            <div className="py-20 text-center">
              <Loader2 className="w-8 h-8 animate-spin text-[#0052FF] mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-600">Loading complete employee details…</p>
            </div>
          ) : activeTab === 'details' ? (
            /* TAB 1: EMPLOYEE DETAILS */
            <div className="space-y-5" data-testid="employee-details-tab">
              
              {/* Basic Information */}
              <CardSection icon={Building} title="Basic Information">
                <GridItem label="Employee Code" value={ed.employee_code || emp.employee_code} />
                <GridItem label="Employee ID" value={emp.id || emp._id} />
                <GridItem label="Full Name" value={`${pd.first_name || emp.first_name || ''} ${pd.last_name || emp.last_name || ''}`.trim()} />
                <GridItem label="Company" value={company.name} />
                <GridItem label="Branch / Location" value={ed.location_name || ed.location || emp.location} />
                <GridItem label="Department" value={ed.department_name || ed.department || emp.department} />
                <GridItem label="Designation" value={ed.designation_name || ed.designation || emp.designation} />
                <GridItem label="Payroll Group" value={ed.payroll_group || ed.ctc_group_code || emp.payroll_group} />
                <GridItem label="Reporting Manager" value={ed.reporting_manager_name || ed.reporting_manager || emp.reporting_manager_name} />
                <GridItem label="Employment Type" value={ed.employment_type_name || ed.employment_type || emp.employment_type} />
                <GridItem label="Employee Category" value={ed.employee_category_name || ed.employee_category || emp.employee_category} />
                <GridItem label="Grade" value={ed.grade_name || ed.grade || emp.grade} />
                <GridItem label="Shift" value={ed.shift_name || ed.shift || emp.shift} />
                <GridItem label="Work Location" value={ed.location_name || ed.location || emp.location} />
                <GridItem label="Status" value={ed.employment_status || emp.employment_status} />
                <GridItem label="Date of Joining" value={ed.date_of_joining || ed.doj || emp.date_of_joining} />
                <GridItem label="Confirmation Date" value={ed.confirmation_date || emp.confirmation_date} />
                <GridItem label="Probation Status" value={ed.probation_status || emp.probation_status} />
                {(ed.exit_date || emp.exit_date) && <GridItem label="Exit Date" value={ed.exit_date || emp.exit_date} />}
              </CardSection>

              {/* Personal Information */}
              <CardSection icon={User} title="Personal Information">
                <GridItem label="Father's Name" value={pd.father_name || pd.father_first_name || emp.father_name} />
                <GridItem label="Mother's Name" value={pd.mother_name || emp.mother_name} />
                <GridItem label="Gender" value={pd.gender || emp.gender} />
                <GridItem label="Date of Birth" value={dobVal} />
                <GridItem label="Age" value={ageDisplay} />
                <GridItem label="Blood Group" value={pd.blood_group || emp.blood_group} />
                <GridItem label="Marital Status" value={pd.marital_status || emp.marital_status} />
                <GridItem label="Date of Marriage" value={pd.marriage_date || pd.date_of_marriage || emp.marriage_date} />
                <GridItem label="Nationality" value={pd.nationality || emp.nationality || 'Indian'} />
                <GridItem label="Religion" value={pd.religion || emp.religion} />
                <GridItem label="Community" value={pd.community || emp.community} />
                <MaskedAadhaarItem label="Aadhaar Number" value={kyc.aadhaar_number || pd.aadhaar_number || emp.aadhaar_number} />
                <GridItem label="PAN Number" value={kyc.pan_number || pd.pan_number || emp.pan_number} />
                <GridItem label="Passport Number" value={pd.passport_number || emp.passport_number} />
                <GridItem label="Driving Licence Number" value={pd.driving_license_number || pd.driving_licence_number || emp.driving_license_number} />
              </CardSection>

              {/* Contact Details */}
              <CardSection icon={Phone} title="Contact Details">
                <GridItem label="Mobile Number" value={pd.mobile || cd.mobile || emp.mobile || emp.phone} />
                <GridItem label="Alternate Mobile" value={cd.alternate_mobile || emp.alternate_mobile} />
                <GridItem label="Personal Email" value={pd.personal_email || cd.personal_email || emp.personal_email || emp.email} />
                <GridItem label="Official Email" value={ed.work_email || cd.official_email || emp.work_email || emp.official_email} />
                <GridItem label="Current Address" value={pd.current_address || cd.current_address || emp.current_address} fullWidth />
                <GridItem label="Permanent Address" value={pd.permanent_address || cd.permanent_address || emp.permanent_address} fullWidth />
                <GridItem label="Emergency Contact Name" value={pd.emergency_contact_name || cd.emergency_contact_name || emp.emergency_contact_name} />
                <GridItem label="Emergency Contact Number" value={pd.emergency_contact_number || cd.emergency_contact_number || emp.emergency_contact_number} />
                <GridItem label="Relationship" value={pd.emergency_contact_relationship || cd.relationship || emp.emergency_contact_relationship} />
              </CardSection>

              {/* Bank & Statutory Details */}
              <CardSection icon={Shield} title="Bank &amp; Statutory Details">
                <GridItem label="Account Holder Name" value={bd.account_holder_name || `${pd.first_name || emp.first_name || ''} ${pd.last_name || emp.last_name || ''}`.trim()} />
                <GridItem label="Bank Name" value={bd.bank_name || emp.bank_name} />
                <GridItem label="Account Number" value={bd.account_number || bd.bank_account_number || emp.account_number || emp.bank_account_number} />
                <GridItem label="IFSC Code" value={bd.ifsc_code || bd.ifsc || emp.ifsc_code || emp.ifsc} />
                <GridItem label="Branch Name" value={bd.branch_name || bd.bank_branch || emp.branch_name || emp.bank_branch} />
                <GridItem label="UAN Number" value={kyc.uan_number || bd.uan_number || emp.uan_number} />
                <GridItem label="PF Number" value={kyc.pf_number || kyc.pf_code || emp.pf_number || emp.pf_code} />
                <GridItem label="ESI Number" value={kyc.esi_number || kyc.esi_code || emp.esi_number || emp.esi_code} />
              </CardSection>

              {/* Nominee Details */}
              <CardSection icon={Users} title="Nominee Details">
                <GridItem label="Nominee Name" value={emp.nominee?.name || emp.nominee?.nominee_name || pd.nominee_name || emp.nominee_name} />
                <GridItem label="Relationship" value={emp.nominee?.relationship || pd.nominee_relationship || emp.nominee_relationship} />
                <GridItem label="Nominee Share %" value={emp.nominee?.percentage ? `${emp.nominee.percentage}%` : (pd.nominee_percentage ? `${pd.nominee_percentage}%` : '')} />
                <GridItem label="Contact Number" value={emp.nominee?.phone || pd.nominee_phone} />
              </CardSection>

              {/* Qualifications */}
              <CardSection icon={GraduationCap} title="Qualification Details">
                {(data?.qualifications?.length > 0 || data?.education?.length > 0) ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                        <tr>
                          <th className="p-2.5">Qualification</th>
                          <th className="p-2.5">Degree</th>
                          <th className="p-2.5">Institution</th>
                          <th className="p-2.5">University / Board</th>
                          <th className="p-2.5">Specialisation</th>
                          <th className="p-2.5">Passing Year</th>
                          <th className="p-2.5">% / CGPA</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(data?.qualifications || data?.education || []).map((q, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2.5 font-semibold text-slate-900">{q.qualification || q.degree || '—'}</td>
                            <td className="p-2.5 font-medium">{q.degree || q.qualification || '—'}</td>
                            <td className="p-2.5">{q.institution || q.college || '—'}</td>
                            <td className="p-2.5">{q.university || q.board || '—'}</td>
                            <td className="p-2.5">{q.specialisation || '—'}</td>
                            <td className="p-2.5 font-mono">{q.passing_year || q.year_of_passing || q.year || '—'}</td>
                            <td className="p-2.5 font-mono font-semibold text-emerald-600">{q.percentage || q.cgpa || q.grade || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No qualification records uploaded.</p>
                )}
              </CardSection>

              {/* Other Certifications */}
              <CardSection icon={Award} title="Other Certifications">
                {data?.certifications?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                        <tr>
                          <th className="p-2.5">Course Name</th>
                          <th className="p-2.5">Institute</th>
                          <th className="p-2.5">Duration</th>
                          <th className="p-2.5">Completed</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(data?.certifications || []).map((c, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2.5 font-medium">{c.course || c.course_name}</td>
                            <td className="p-2.5">{c.institute}</td>
                            <td className="p-2.5">{c.duration}</td>
                            <td className="p-2.5 font-semibold text-emerald-600">{c.completed ? 'Yes' : 'In Progress'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No certification records uploaded.</p>
                )}
              </CardSection>

              {/* Work Experience */}
              <CardSection icon={Briefcase} title="Work Experience">
                {(data?.work_experience?.length > 0 || data?.previous_employers?.length > 0) ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                        <tr>
                          <th className="p-2.5">Employer Name</th>
                          <th className="p-2.5">Designation</th>
                          <th className="p-2.5">Dept / Type</th>
                          <th className="p-2.5">From Date</th>
                          <th className="p-2.5">To Date</th>
                          <th className="p-2.5">Total Exp</th>
                          <th className="p-2.5">Last Salary</th>
                          <th className="p-2.5">Reason for Leaving</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(data?.work_experience || data?.previous_employers || []).map((w, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2.5 font-medium text-slate-900">{w.employer_name || w.company || w.company_name || '—'}</td>
                            <td className="p-2.5">{w.designation || '—'}</td>
                            <td className="p-2.5">{w.department || w.employment_type || '—'}</td>
                            <td className="p-2.5 font-mono">{w.from_date || '—'}</td>
                            <td className="p-2.5 font-mono">{w.to_date || '—'}</td>
                            <td className="p-2.5 font-semibold text-[#0052FF]">{w.experience || '—'}</td>
                            <td className="p-2.5 font-mono">{w.salary ? `₹ ${w.salary}` : (w.last_salary ? `₹ ${Number(w.last_salary).toLocaleString('en-IN')}` : '—')}</td>
                            <td className="p-2.5">{w.reason || w.reason_for_leaving || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No prior experience records listed.</p>
                )}
              </CardSection>

              {/* Family Details */}
              <CardSection icon={Users} title="Family Details">
                {data?.family_details?.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 text-slate-700 uppercase font-semibold">
                        <tr>
                          <th className="p-2.5">Full Name</th>
                          <th className="p-2.5">Relationship</th>
                          <th className="p-2.5">Date of Birth</th>
                          <th className="p-2.5">Occupation</th>
                          <th className="p-2.5">Contact Number</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {(data?.family_details || []).map((f, i) => (
                          <tr key={i} className="hover:bg-slate-50">
                            <td className="p-2.5 font-medium">{f.name || f.full_name}</td>
                            <td className="p-2.5">{f.relationship}</td>
                            <td className="p-2.5 font-mono">{f.dob || f.date_of_birth}</td>
                            <td className="p-2.5">{f.occupation}</td>
                            <td className="p-2.5 font-mono">{f.contact_number || f.mobile}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No family member records listed.</p>
                )}
              </CardSection>

              {/* Documents Repository */}
              <CardSection icon={FileText} title="Document Repository &amp; File Attachments">
                {data?.documents?.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {data.documents.map((d, i) => (
                      <div key={i} className="border border-slate-200 rounded-lg p-3 bg-white flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded bg-blue-50 text-[#0052FF] flex items-center justify-center font-bold text-xs uppercase">
                            {d.document_type?.[0] || 'DOC'}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-slate-800 truncate">{d.name || d.filename}</p>
                            <p className="text-[10px] text-slate-400">{d.document_category || 'Attachment'} • {d.created_at?.slice(0, 10)}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <a
                            href={d.file_url || `${API_URL}/employees/${employeeId}/appointment-letter/pdf`}
                            target="_blank"
                            rel="noreferrer"
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title="Preview File"
                          >
                            <Eye className="w-4 h-4" />
                          </a>
                          <a
                            href={d.file_url || `${API_URL}/employees/${employeeId}/appointment-letter/pdf`}
                            download
                            className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded"
                            title="Download File"
                          >
                            <Download className="w-4 h-4" />
                          </a>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-400 italic">No documents uploaded in repository.</p>
                )}
              </CardSection>

            </div>
          ) : (
            /* TAB 2: CTC BREAKUP & APPOINTMENT LETTER */
            <div className="space-y-6" data-testid="ctc-breakup-tab">
              
              {/* CTC Summary Overview */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-4">
                <div className="flex items-center justify-between border-b border-slate-100 pb-3">
                  <div>
                    <h3 className="text-sm font-bold text-slate-900 flex items-center gap-2">
                      <Wallet className="w-4 h-4 text-[#0052FF]" />
                      Salary Structure &amp; CTC Summary
                    </h3>
                    <p className="text-xs text-slate-500">
                      Exact salary breakup saved in CTC Planner for {pd.first_name} {pd.last_name}
                    </p>
                  </div>

                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100 text-blue-800 border border-blue-200 uppercase font-mono">
                      Structure: <strong>{ctc.structure_source ? ctc.structure_source.toUpperCase() : (ctc.structure_code || 'GROUP')}</strong>
                    </span>
                    <span className={`text-xs font-bold px-3 py-1 rounded-full border flex items-center gap-1.5 ${
                      isCTCApproved ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                    }`}>
                      {isCTCApproved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
                      {isCTCApproved ? 'Approved CTC' : 'Pending HR Review'}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard title="Annual CTC" value={ctc.ctc_annual} subtitle="Per Annum Cost" highlight />
                  <StatCard title="Monthly CTC" value={ctc.ctc_monthly} subtitle="Monthly CTC" />
                  <StatCard title="Fixed Gross" value={ctc.gross_monthly} subtitle="Monthly Fixed" />
                  <StatCard title="Net Monthly Pay" value={ctc.net_monthly} subtitle="Take Home" />
                  <StatCard title="Basic (Monthly)" value={(ctc.components || []).find(c => ['BASIC', 'BASIC_SALARY'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('basic'))?.monthly_amount || (ctc.components || []).find(c => ['BASIC', 'BASIC_SALARY'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('basic'))?.monthly} subtitle="Base Earning" />
                  <StatCard title="HRA (Monthly)" value={(ctc.components || []).find(c => ['HRA', 'HOUSE_RENT'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('house rent'))?.monthly_amount || (ctc.components || []).find(c => ['HRA', 'HOUSE_RENT'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('house rent'))?.monthly} subtitle="House Rent Allowance" />
                  <StatCard title="Special Allowance" value={(ctc.components || []).find(c => ['SPA', 'SPECIAL', 'SPECIAL_ALLOWANCE'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('special'))?.monthly_amount || (ctc.components || []).find(c => ['SPA', 'SPECIAL', 'SPECIAL_ALLOWANCE'].includes((c.head_code || '').toUpperCase()) || (c.head_name || '').toLowerCase().includes('special'))?.monthly} subtitle="Special Allowance" />
                  <StatCard title="Employer Contribution" value={ctc.total_employer_monthly} subtitle="Employer PF/ESI" />
                  <StatCard title="Effective Date" rawValue={ctc.effective_from || ed.date_of_joining || '—'} subtitle="Effective Date" />
                  <StatCard title="Approval Status" rawValue={ctc.approval_status ? ctc.approval_status.toUpperCase() : (isCTCApproved ? 'APPROVED' : 'PENDING')} subtitle="HR Review Status" />
                  <StatCard title="Approved By" rawValue={ctc.approved_by || 'HR Admin'} subtitle="Approver" />
                  <StatCard title="Approved Date" rawValue={ctc.approved_at || ctc.approval_date || ctc.created_at ? String(ctc.approved_at || ctc.approval_date || ctc.created_at).slice(0, 10) : '—'} subtitle="Date Approved" />
                </div>
              </div>

              {/* Detailed Salary Head Breakup Table */}
              <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Salary Head Component Breakup</h4>
                <div className="overflow-x-auto border border-slate-200 rounded-lg">
                  <table className="w-full text-xs text-left">
                    <thead className="bg-slate-900 text-white font-semibold uppercase">
                      <tr>
                        <th className="p-3">Salary Head</th>
                        <th className="p-3">Head Type</th>
                        <th className="p-3 text-right">Monthly Amount (₹)</th>
                        <th className="p-3 text-right">Annual Amount (₹)</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-200">
                      {ctc.components && ctc.components.length > 0 ? (
                        ctc.components.map((c, i) => {
                          const mAmt = c.monthly_amount || c.monthly || 0;
                          const aAmt = c.annual_amount || c.annual || (mAmt * 12);
                          return (
                            <tr key={i} className="hover:bg-slate-50">
                              <td className="p-3 font-semibold text-slate-900">
                                {c.head_name || c.head_code}
                                <span className="text-[10px] text-slate-400 font-mono ml-2">({c.head_code})</span>
                              </td>
                              <td className="p-3">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase ${
                                  c.head_type === 'deduction' ? 'bg-red-50 text-red-700' :
                                  c.head_type === 'employer' ? 'bg-violet-50 text-violet-700' :
                                  'bg-emerald-50 text-emerald-700'
                                }`}>
                                  {c.head_type || 'Earning'}
                                </span>
                              </td>
                              <td className="p-3 text-right font-mono font-medium text-slate-900">₹ {mAmt.toLocaleString('en-IN')}</td>
                              <td className="p-3 text-right font-mono font-medium text-slate-900">₹ {aAmt.toLocaleString('en-IN')}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr>
                          <td colSpan="4" className="p-4 text-center text-slate-400 italic">
                            No component breakdown found. Please update salary breakup in CTC Planner.
                          </td>
                        </tr>
                      )}
                    </tbody>
                    <tfoot className="bg-slate-100 font-bold border-t-2 border-slate-300 divide-y divide-slate-200">
                      {ctc.total_earnings_monthly > 0 && (
                        <tr>
                          <td colSpan="2" className="p-2.5 text-slate-700">Total Fixed Gross (Earnings)</td>
                          <td className="p-2.5 text-right font-mono text-emerald-700">₹ {Number(ctc.gross_monthly || ctc.total_earnings_monthly || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 text-right font-mono text-emerald-700">₹ {Number((ctc.gross_monthly || ctc.total_earnings_monthly || 0) * 12).toLocaleString('en-IN')}</td>
                        </tr>
                      )}
                      {ctc.total_deductions_monthly > 0 && (
                        <tr>
                          <td colSpan="2" className="p-2.5 text-slate-700">Total Employee Deductions</td>
                          <td className="p-2.5 text-right font-mono text-red-700">₹ {Number(ctc.total_deductions_monthly || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 text-right font-mono text-red-700">₹ {Number((ctc.total_deductions_monthly || 0) * 12).toLocaleString('en-IN')}</td>
                        </tr>
                      )}
                      {ctc.total_employer_monthly > 0 && (
                        <tr>
                          <td colSpan="2" className="p-2.5 text-slate-700">Total Employer Contributions</td>
                          <td className="p-2.5 text-right font-mono text-violet-700">₹ {Number(ctc.total_employer_monthly || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 text-right font-mono text-violet-700">₹ {Number((ctc.total_employer_monthly || 0) * 12).toLocaleString('en-IN')}</td>
                        </tr>
                      )}
                      {ctc.net_monthly > 0 && (
                        <tr>
                          <td colSpan="2" className="p-2.5 text-slate-900">Net Monthly Salary (Take Home)</td>
                          <td className="p-2.5 text-right font-mono text-emerald-800">₹ {Number(ctc.net_monthly || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2.5 text-right font-mono text-emerald-800">₹ {Number((ctc.net_monthly || 0) * 12).toLocaleString('en-IN')}</td>
                        </tr>
                      )}
                      <tr className="bg-slate-200">
                        <td colSpan="2" className="p-3 text-slate-900 text-sm">TOTAL COST TO COMPANY (CTC)</td>
                        <td className="p-3 text-right font-mono text-[#0052FF] text-sm font-bold">
                          ₹ {Number(ctc.ctc_monthly || (ctc.ctc_annual ? ctc.ctc_annual / 12 : 0)).toLocaleString('en-IN')}
                        </td>
                        <td className="p-3 text-right font-mono text-[#0052FF] text-sm font-bold">
                          ₹ {Number(ctc.ctc_annual || 0).toLocaleString('en-IN')}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              {/* Historical Version Timeline */}
              {ctc.history && ctc.history.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
                  <h4 className="text-xs font-bold text-slate-800 uppercase tracking-wider">Historical CTC Versions Timeline</h4>
                  <div className="overflow-x-auto border border-slate-200 rounded-lg">
                    <table className="w-full text-xs text-left">
                      <thead className="bg-slate-100 text-slate-700 font-semibold uppercase">
                        <tr>
                          <th className="p-2.5">Structure Source</th>
                          <th className="p-2.5">Effective From</th>
                          <th className="p-2.5 text-right">Annual CTC (₹)</th>
                          <th className="p-2.5 text-right">Monthly Gross (₹)</th>
                          <th className="p-2.5">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200">
                        {ctc.history.map((h, idx) => (
                          <tr key={idx} className="hover:bg-slate-50">
                            <td className="p-2.5 font-bold text-slate-900 font-mono uppercase">
                              {h.structure_source || h.structure_code || 'GROUP'}
                            </td>
                            <td className="p-2.5 font-mono">{h.effective_from || '—'}</td>
                            <td className="p-2.5 text-right font-mono font-semibold text-slate-900">₹ {Number(h.ctc_annual || 0).toLocaleString('en-IN')}</td>
                            <td className="p-2.5 text-right font-mono text-slate-700">₹ {Number(h.gross_monthly || (h.ctc_annual ? h.ctc_annual / 12 : 0)).toLocaleString('en-IN')}</td>
                            <td className="p-2.5">
                              <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                                h.is_active !== false ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-slate-100 text-slate-600'
                              }`}>
                                {h.is_active !== false ? 'ACTIVE' : 'HISTORICAL'}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Approval Workflow & Appointment Letter Generation Section */}
              <div className="bg-white border border-blue-200 rounded-xl p-5 shadow-sm space-y-4">
                
                {/* Flow Indicator */}
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-xs text-slate-600 flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold text-slate-800">Workflow Approval Flow:</span>
                  <div className="flex items-center gap-2 font-mono text-[11px]">
                    <span className="text-slate-500">1. Employee Created</span>
                    <span>➔</span>
                    <span className="text-slate-500">2. CTC Assigned</span>
                    <span>➔</span>
                    <span className={`font-semibold ${isCTCApproved ? 'text-emerald-600' : 'text-amber-600'}`}>
                      3. HR Approval {isCTCApproved ? '✓' : '(Pending)'}
                    </span>
                    <span>➔</span>
                    <span className={`font-semibold ${apptLetter.generated ? 'text-emerald-600' : 'text-blue-600'}`}>
                      4. Official Letter
                    </span>
                  </div>
                </div>

                {!isCTCApproved ? (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <AlertCircle className="w-6 h-6 text-amber-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-amber-900">CTC Approval Required</p>
                        <p className="text-[11px] text-amber-700 mt-0.5">
                          The assigned CTC breakup must be reviewed and approved by HR before the Official Letter can be generated.
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={handleApproveCTC}
                      disabled={approving}
                      data-testid="approve-ctc-btn"
                      className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-semibold shadow shrink-0 flex items-center gap-1.5"
                    >
                      {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileCheck className="w-4 h-4" />}
                      Approve CTC Breakup
                    </button>
                  </div>
                ) : (
                  <div className="bg-emerald-50/60 border border-emerald-200 rounded-lg p-4 flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-3">
                      <CheckCircle2 className="w-6 h-6 text-emerald-600 shrink-0" />
                      <div>
                        <p className="text-xs font-bold text-emerald-900">CTC Approved &amp; Ready for Official Letter</p>
                        <p className="text-[11px] text-emerald-700 mt-0.5">
                          Approved by HR on {ctc.approved_at?.slice(0, 10) || 'Current Session'}. You can now generate, print, or email the Official Letter.
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={handleGenerateAppointmentLetter}
                        disabled={generating}
                        data-testid="generate-appointment-letter-btn"
                        className="px-4 py-2 bg-[#0052FF] hover:bg-[#0043D1] text-white rounded-lg text-xs font-semibold shadow flex items-center gap-2"
                      >
                        {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                        {apptLetter.generated ? 'Re-Generate Letter' : 'Generate Official Letter'}
                      </button>
                    </div>
                  </div>
                )}

                {/* PDF Actions & In-Browser Preview */}
                {pdfPreviewUrl && (
                  <div className="border border-slate-200 rounded-lg overflow-hidden space-y-3 bg-slate-900 text-white p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-400" />
                        <span className="text-xs font-bold">Generated Official Letter PDF</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleDownloadPdf}
                          className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded text-xs font-medium flex items-center gap-1.5 cursor-pointer"
                          data-testid="download-appointment-letter-btn"
                        >
                          <Download className="w-3.5 h-3.5" /> Download PDF
                        </button>
                        <button
                          onClick={handlePrint}
                          className="px-3 py-1.5 bg-slate-700 hover:bg-slate-600 text-white rounded text-xs font-medium flex items-center gap-1.5"
                          data-testid="print-appointment-letter-btn"
                        >
                          <Printer className="w-3.5 h-3.5" /> Print
                        </button>
                        <button
                          onClick={handleEmailAppointmentLetter}
                          disabled={emailing}
                          className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs font-medium flex items-center gap-1.5"
                          data-testid="email-appointment-letter-btn"
                        >
                          {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                          Email to Employee
                        </button>
                      </div>
                    </div>

                    <div className="bg-slate-800 rounded border border-slate-700 h-96 overflow-hidden">
                      <iframe
                        src={pdfPreviewUrl}
                        title="Official Letter Preview"
                        className="w-full h-full border-0"
                      />
                    </div>
                  </div>
                )}

              </div>

            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-slate-100 border-t border-slate-200 p-4 flex items-center justify-between shrink-0">
          <p className="text-xs text-slate-500">
            Employee 360° Profile • Tenant: {company.name || 'HECS Ltd'}
          </p>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-xs font-semibold"
            data-testid="close-view-profile-footer"
          >
            Close View
          </button>
        </div>

      </div>
    </div>
  );
}

function CardSection({ icon: Icon, title, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm space-y-3">
      <h3 className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-2 border-b border-slate-100 pb-2">
        <Icon className="w-4 h-4 text-[#0052FF]" />
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
        {children}
      </div>
    </div>
  );
}

function GridItem({ label, value, fullWidth }) {
  const hasVal = value !== undefined && value !== null && String(value).trim() !== '' && String(value).trim() !== 'undefined';
  return (
    <div className={fullWidth ? 'sm:col-span-2 md:col-span-3' : ''}>
      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      <p className={`text-xs font-semibold mt-0.5 ${hasVal ? 'text-slate-800' : 'text-slate-400 italic font-normal'}`}>
        {hasVal ? value : '—'}
      </p>
    </div>
  );
}

function StatCard({ title, value, rawValue, subtitle, highlight }) {
  const displayVal = rawValue !== undefined && rawValue !== null && rawValue !== '' ? rawValue : (value !== undefined && value !== null && value !== '' ? `₹ ${Number(value).toLocaleString('en-IN')}` : '—');
  return (
    <div className={`p-3.5 rounded-lg border ${highlight ? 'bg-blue-50 border-blue-200 text-[#0052FF]' : 'bg-slate-50 border-slate-200 text-slate-800'}`}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{title}</p>
      <p className="text-base font-bold font-mono mt-1">{displayVal}</p>
      <p className="text-[10px] text-slate-400 mt-0.5">{subtitle}</p>
    </div>
  );
}

function MaskedAadhaarItem({ label, value, fullWidth }) {
  const [revealed, setRevealed] = useState(false);
  const raw = value !== undefined && value !== null && String(value).trim() !== '' && String(value).trim() !== 'undefined' ? String(value).trim() : '';
  const digits = raw.replace(/\D/g, '');
  const masked = digits.length >= 4 ? `XXXX-XXXX-${digits.slice(-4)}` : (raw ? 'XXXX-XXXX-XXXX' : '—');
  return (
    <div className={fullWidth ? 'sm:col-span-2 md:col-span-3' : ''}>
      <p className="text-[10px] font-medium text-slate-400 uppercase tracking-wide">{label}</p>
      <div className="flex items-center gap-1.5 mt-0.5">
        <p className={`text-xs font-semibold ${raw ? 'text-slate-800 font-mono' : 'text-slate-400 italic font-normal'}`}>
          {raw ? (revealed ? raw : masked) : '—'}
        </p>
        {raw && (
          <button
            type="button"
            onClick={() => setRevealed(!revealed)}
            className="text-slate-400 hover:text-slate-600 transition p-0.5"
            title={revealed ? "Hide Aadhaar" : "Reveal Aadhaar"}
            data-testid="toggle-view-aadhaar"
          >
            {revealed ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </button>
        )}
      </div>
    </div>
  );
}
